import { Checkbox, FormHelperText, alpha, styled, useTheme } from "@mui/material"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import OutlinedInput from "@mui/material/OutlinedInput"
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select"
import { ReactNode, useState } from "react"
import { IoMdCloseCircle } from "react-icons/io"

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const Root = styled(FormControl)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& .MuiInputBase-root": {
    border: "1px solid #d0d5dd",
    marginTop: "3px",
    padding: 0,
  },
  "& .MuiInputBase-input": {
    padding: "6px 12px",
    borderRadius: "4px",
  },
  "& .MuiInputBase-root.Mui-focused": {
    border: `1px solid ${theme.palette.secondary.main}`,
    boxShadow: `${alpha(theme.palette.secondary.main, 0.5)} 0px 0px 0px 0.2rem`,
    borderRadius: "4px",
  },
  "& .MuiInputBase-root.Mui-error": {
    border: `1px solid ${theme.palette.error.main}`,
    boxShadow: `${alpha(theme.palette.error.main, 0.3)} 0px 0px 0px 0.2rem`,
    borderRadius: "4px",
  },
  "& .MuiFormLabel-root": {
    position: "relative",
    transform: "initial",
    fontWeight: "500",
    fontSize: "14px",
  },
  "& fieldset": {
    display: "none",
  },
}))

interface IProps extends Omit<SelectProps, "variant" | "onChange"> {
  values: { value: number | string; name: string; disabled?: boolean }[]
  helperText?: ReactNode
  label?: string
  checkbox?: boolean
  onChange: (e: { target: { value: any; name?: string } }) => void
  isChip?: boolean
}

const MultipleSelect = (props: IProps) => {
  const theme = useTheme()
  const { sx, checkbox, isChip, helperText, ...rest } = props
  const [selectValue, setSelectValue] = useState<any[]>((props?.value as any) ?? [])
  const { fullWidth, error } = props

  const valuesObject: { [keyof in string]: string } = props.values.reduce(
    (acc, { value, name }) => ({ ...acc, [value]: name }),
    {},
  )

  const handleDelete = (value: number | string) => {
    const newValue = (props.value as number[]).filter((group: number) => group !== value)
    props.onChange({
      target: {
        value: newValue,
        name: props.name,
      },
    })
  }

  const onChange = (e: SelectChangeEvent<unknown>) => {
    setSelectValue(e.target.value as any[])
    props.onChange(e as any)
  }

  return (
    <Root color="secondary" fullWidth={fullWidth} error={error} sx={sx}>
      {props.label && <InputLabel>{props.label}</InputLabel>}
      <Select
        multiple
        displayEmpty
        input={<OutlinedInput label="Chip" />}
        renderValue={(selected: any) => {
          if (!selected.length && props.placeholder) {
            return <span style={{ color: "#a2a2a2" }}>{props.placeholder}</span>
          }

          if (isChip) {
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value: any) => (
                  <Chip
                    key={value}
                    label={valuesObject[value]}
                    sx={{
                      background: theme.palette.secondary.main,
                      color: "#fff",
                      height: "auto",
                    }}
                    onDelete={() => handleDelete(value)}
                    deleteIcon={
                      <IoMdCloseCircle
                        color="#fff"
                        onMouseDown={event => event.stopPropagation()}
                      />
                    }
                  />
                ))}
              </Box>
            )
          }

          return selected.map((group: any) => valuesObject[group]).join(", ")
        }}
        MenuProps={MenuProps}
        value={props?.value ?? selectValue}
        {...rest}
        fullWidth
        onChange={onChange}
      >
        {props.values.map(({ value, name, disabled }) => (
          <MenuItem key={value} value={value} disabled={!!disabled} color="secondary">
            {checkbox && !disabled && (
              <Checkbox
                size="small"
                color="secondary"
                checked={
                  ((props?.value as number[] | string[]) ?? selectValue)?.indexOf(value as never) >
                  -1
                }
                sx={{ p: 0, pr: "9px" }}
              />
            )}
            {name}
          </MenuItem>
        ))}
      </Select>
      {props.error && <FormHelperText>{helperText ?? ""}</FormHelperText>}
    </Root>
  )
}

export default MultipleSelect
